<template>
  <div class="koneko-budget-history-panel mt-4">
    <div class="d-flex align-center mb-2" style="gap: 8px">
      <span class="caption font-weight-medium">Budget change history</span>
      <v-btn
        x-small
        outlined
        color="primary"
        :loading="loading"
        @click="loadHistory"
      >
        Refresh
      </v-btn>
    </div>
    <p v-if="!enabled" class="caption grey--text text--darken-1 mb-0">
      History is available when personal budgets are saved to NekoHR.
    </p>
    <p v-else-if="unavailable" class="caption grey--text text--darken-1 mb-0">
      {{ unavailableMessage }}
    </p>
    <v-alert v-else-if="error" type="error" dense outlined class="mb-2">
      {{ error }}
    </v-alert>
    <template v-else-if="changes.length">
      <p class="caption grey--text text--darken-1 mb-2">
        The table above is your <strong>current</strong> caps. Below is an audit
        log (newest first) — each save can add several lines per project (min
        and max separately).
      </p>
      <div
        v-for="group in groupedChanges"
        :key="'bh-group-' + group.project"
        class="mb-3"
      >
        <div class="caption font-weight-medium mb-1">{{ group.project }}</div>
        <v-simple-table
          dense
          class="koneko-budget-cap-table budget-history-table"
        >
          <thead>
            <tr>
              <th class="koneko-col-when">When</th>
              <th class="koneko-col-field">Field</th>
              <th class="koneko-col-hours">From</th>
              <th class="koneko-col-hours">To</th>
              <th class="koneko-col-by">By</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in group.rows"
              :key="'bh-' + group.project + '-' + idx"
            >
              <td class="text-no-wrap">{{ formatWhen(row.at) }}</td>
              <td>{{ row.field }}</td>
              <td>{{ formatHoursCell(row.oldValue) }}</td>
              <td>{{ formatHoursCell(row.newValue) }}</td>
              <td>{{ row.changedBy || "—" }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
    </template>
    <p v-else-if="!loading" class="caption grey--text text--darken-1 mb-0">
      No recorded changes for {{ calendarMonthKey }} yet. History only logs
      saves after this feature was deployed — edit a cap and click
      <strong>Save</strong> to create the first entry.
    </p>
  </div>
</template>

<script>
import {
  fetchMyBudgetHistory,
  fetchRosterBudgetHistory,
} from "@/services/konekoABudgetApi";

function formatHoursCell(value) {
  const s = String(value || "").trim();
  if (!s) return "0:00";
  return s;
}

export default {
  name: "KonekoABudgetHistoryPanel",
  props: {
    calendarMonthKey: {type: String, required: true},
    enabled: {type: Boolean, default: false},
    /** Roster / planning key for the person whose history to load (view-as or self). */
    subjectUsername: {type: String, default: ""},
    /** When true, use `…/me/budget-history/`; otherwise `…/roster/budget-history/?user=`. */
    forSelf: {type: Boolean, default: true},
    /** Project order from the editor table above (keeps groups aligned). */
    projectOrder: {type: Array, default: () => []},
  },
  data() {
    return {
      loading: false,
      error: "",
      unavailable: false,
      unavailableMessage: "",
      changes: [],
    };
  },
  computed: {
    groupedChanges() {
      const order = Array.isArray(this.projectOrder)
        ? this.projectOrder.map((p) => String(p || "").trim()).filter(Boolean)
        : [];
      const byProject = {};
      (this.changes || []).forEach((row) => {
        const p = String(row.project || "").trim();
        if (!p) return;
        if (!byProject[p]) byProject[p] = [];
        byProject[p].push(row);
      });
      const seen = new Set();
      const projects = [];
      order.forEach((p) => {
        if (byProject[p] && byProject[p].length) {
          projects.push(p);
          seen.add(p);
        }
      });
      Object.keys(byProject)
        .sort((a, b) => a.localeCompare(b))
        .forEach((p) => {
          if (!seen.has(p)) projects.push(p);
        });
      return projects.map((project) => ({
        project,
        rows: byProject[project] || [],
      }));
    },
  },
  watch: {
    calendarMonthKey() {
      if (this.enabled) this.loadHistory();
    },
    enabled(v) {
      if (v) this.loadHistory();
    },
    subjectUsername() {
      if (this.enabled) this.loadHistory();
    },
    forSelf() {
      if (this.enabled) this.loadHistory();
    },
  },
  mounted() {
    if (this.enabled) this.loadHistory();
  },
  methods: {
    formatHoursCell,
    async loadHistory() {
      if (!this.enabled || !this.calendarMonthKey) return;
      this.loading = true;
      this.error = "";
      this.unavailable = false;
      this.unavailableMessage = "";
      try {
        const res = this.forSelf
          ? await fetchMyBudgetHistory(this.calendarMonthKey)
          : await fetchRosterBudgetHistory(
              this.calendarMonthKey,
              this.subjectUsername
            );
        this.changes = Array.isArray(res.changes) ? res.changes : [];
      } catch (e) {
        const status = e && e.response && e.response.status;
        if (status === 404) {
          this.changes = [];
          this.unavailable = true;
          this.unavailableMessage = this.forSelf
            ? "Change history is not available from the API yet (endpoint not deployed)."
            : "Change history for this person is not available from the API yet — the server may only support your own history, or the roster audit route is not deployed.";
          return;
        }
        this.error =
          (e && e.response && e.response.data && e.response.data.error) ||
          (e && e.message) ||
          "Failed to load budget history";
        this.changes = [];
      } finally {
        this.loading = false;
      }
    },
    formatWhen(iso) {
      if (!iso) return "—";
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return String(iso);
      }
    },
    /** Parent calls after a successful personal budget save. */
    refresh() {
      return this.loadHistory();
    },
  },
};
</script>

<style scoped>
.koneko-budget-cap-table >>> table {
  table-layout: fixed;
  width: 100%;
}
.koneko-budget-cap-table >>> th,
.koneko-budget-cap-table >>> td {
  font-size: 12px;
  vertical-align: top;
}
.koneko-col-when {
  width: 34%;
}
.koneko-col-field {
  width: 10%;
}
.koneko-col-hours {
  width: 14%;
}
.koneko-col-by {
  width: 14%;
}
</style>
